import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import MarketingShell from './components/MarketingShell';
import AppShell from './components/AppShell';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import PricingPage from './pages/PricingPage';
import SuccessPage from './pages/SuccessPage';
import OnboardingPage from './pages/OnboardingPage';
import LearnPage from './pages/LearnPage';
import SkillPathsPage from './pages/SkillPathsPage';
import SkillPathDetailPage from './pages/SkillPathDetailPage';
import LessonPage from './pages/LessonPage';
import WorkLibraryPage from './pages/WorkLibraryPage';
import AICoachPage from './pages/AICoachPage';
import SettingsPage from './pages/SettingsPage';
import AdminHealthPage from './pages/AdminHealthPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import CertificatePage from './pages/CertificatePage';
import PublicProfilePage from './pages/PublicProfilePage';
import LegalPage from './pages/LegalPage';
import TeamPage from './pages/TeamPage';
import TeamWorkspacePage from './pages/TeamWorkspacePage';
import TeamJoinPage from './pages/TeamJoinPage';
import DrillPage from './pages/DrillPage';
import ChallengePage from './pages/ChallengePage';
import BringYourOwnProblemPage from './pages/BringYourOwnProblemPage';
import TryChallengePage from './pages/TryChallengePage';
import NotFoundPage from './pages/NotFoundPage';
import ErrorBoundary from './components/ErrorBoundary';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-warm-50">
        <div className="text-ink-500 text-sm">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!profile) {
    return (
      <div className="min-h-screen grid place-items-center bg-warm-50">
        <div className="text-ink-500 text-sm">Setting up your account...</div>
      </div>
    );
  }
  if (!profile.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
};

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AuthProvider>
        <ScrollToTop />
        <Toaster position="top-right" closeButton richColors />
        <Routes>
          <Route element={<MarketingShell />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/try" element={<TryChallengePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/coppa" element={<LegalPage kind="coppa" />} />
            <Route path="/gdpr-k" element={<LegalPage kind="gdpr-k" />} />
            <Route path="/subprocessors" element={<LegalPage kind="subprocessors" />} />
          </Route>

          <Route path="/login" element={<LoginPage />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/certificates/:code" element={<CertificatePage />} />
          <Route path="/u/:handle" element={<PublicProfilePage />} />
          <Route path="/team/join/:token" element={<RequireAuth><TeamJoinPage /></RequireAuth>} />
          <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />

          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route path="/learn" element={<LearnPage />} />
            <Route path="/challenges" element={<ChallengePage />} />
            <Route path="/practice" element={<BringYourOwnProblemPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/team/workspace" element={<TeamWorkspacePage />} />
            <Route path="/dashboard" element={<Navigate to="/learn" replace />} />
            <Route path="/progress" element={<Navigate to="/learn" replace />} />
            <Route path="/skill-paths" element={<SkillPathsPage />} />
            <Route path="/skill-paths/:slug" element={<SkillPathDetailPage />} />
            <Route path="/skill-paths/:slug/:lessonSlug" element={<LessonPage />} />
            <Route path="/notebook" element={<WorkLibraryPage />} />
            <Route path="/work-library" element={<Navigate to="/notebook" replace />} />
            <Route path="/workflows" element={<Navigate to="/learn" replace />} />
            <Route path="/workflows/:slug" element={<Navigate to="/learn" replace />} />
            <Route path="/prompt-lab" element={<Navigate to="/learn" replace />} />
            <Route path="/prompt-lab/:slug" element={<Navigate to="/learn" replace />} />
            <Route path="/projects" element={<Navigate to="/learn" replace />} />
            <Route path="/projects/:slug" element={<Navigate to="/learn" replace />} />
            <Route path="/saved" element={<Navigate to="/notebook" replace />} />
            <Route path="/drills" element={<DrillPage />} />
            <Route path="/coach" element={<AICoachPage />} />
            <Route path="/admin/health" element={<AdminHealthPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          <Route path="/for-kids" element={<Navigate to="/" replace />} />
          <Route path="/for-parents" element={<Navigate to="/" replace />} />
          <Route path="/curriculum" element={<Navigate to="/skill-paths" replace />} />
          <Route path="/safety" element={<Navigate to="/" replace />} />
          <Route path="/guardians" element={<Navigate to="/learn" replace />} />
          <Route path="/kids" element={<Navigate to="/learn" replace />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
